import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isDesktopApp, stopDesktopProctoring, submitProctoringData, logCheatingEvent } from "../../utils/desktopBridge";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heading, Text } from '@/components/ui/typography';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { 
  CheckCircle2, 
  Clock, 
  Circle, 
  Bookmark, 
  BookmarkCheck, 
  Code2, 
  List, 
  ChevronLeft, 
  ChevronRight, 
  SkipForward,
  X,
  CheckCircle,
  Bug,
  ChevronDown,
  ChevronUp,
  Copy,
  AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function QuickCodingTest() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const test_id = searchParams.get('test_id');

  const passedQuestions = location.state?.questions || [];
  const passedLanguage = location.state?.backendLanguage || 'python';

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const timerRef = useRef(null);

  // unified answers state for both mcq and code (aligned by questions index)
  const [answers, setAnswers] = useState([]); // null = not answered, string for code or option value for mcq
  const [visited, setVisited] = useState([]); // boolean visited flag
  const [statuses, setStatuses] = useState([]); // 'not-visited' | 'answered' | 'skipped' | 'marked'
  const [currentIdx, setCurrentIdx] = useState(0);
  const [language, setLanguage] = useState('python');
  const autoSaveKey = `qc_autosave_${test_id}`;
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  
  // Network status monitoring
  const { networkStatus, networkSpeed, NetworkIcon } = useNetworkStatus();

  // Keyboard shortcut to toggle debug panel (Ctrl+D / Cmd+D)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        setShowDebugPanel(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (passedQuestions.length > 0) {
      // Use passed questions — no fetch
      setQuestions(passedQuestions);
      setLanguage(passedLanguage);

      setAnswers(new Array(passedQuestions.length).fill(null));
      setVisited(new Array(passedQuestions.length).fill(false));
      setStatuses(new Array(passedQuestions.length).fill('not-visited'));

      setLoading(false);
    } else {
      // Fallback — only fetch if nothing passed
      fetchQuestions();
    }

    const saved = localStorage.getItem(autoSaveKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.answers) setAnswers(parsed.answers);
        if (parsed.visited) setVisited(parsed.visited);
        if (parsed.statuses) setStatuses(parsed.statuses);
        if (typeof parsed.currentIdx === 'number') setCurrentIdx(parsed.currentIdx);
      } catch (e) { /* ignore parse errors */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleSubmit(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const payload = { answers, visited, statuses, currentIdx };
      localStorage.setItem(autoSaveKey, JSON.stringify(payload));
    }, 4000);
    return () => clearInterval(id);
  }, [answers, visited, statuses, currentIdx]);

  // Visibility change detection for proctoring
  useEffect(() => {
    if (!isDesktopApp()) return;

    const handleVisibilityChange = () => {
      const type = document.hidden ? 'window_hidden' : 'window_visible';
      logCheatingEvent(type, 'medium', {
        action: document.hidden ? 'left_window' : 'returned_to_window',
        timestamp: new Date().toISOString(),
        test_id: test_id
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [test_id]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const resp = await fetch(`/api/get_questions?test_id=${encodeURIComponent(test_id)}`);
      if (!resp.ok) throw new Error(`Status ${resp.status}`);
      const data = await resp.json();
      const q = data.question_data || [];
      setQuestions(q);
      setLanguage(data.language || 'python');

      // initialize state arrays
      setAnswers(new Array(q.length).fill(null));
      setVisited(new Array(q.length).fill(false));
      setStatuses(new Array(q.length).fill('not-visited'));

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch questions', err);
      setError('Failed to load questions.');
      setLoading(false);
    }
  };

  const markVisited = (idx) => {
    setVisited(prev => {
      const copy = [...prev];
      copy[idx] = true;
      return copy;
    });
    // Don't change status automatically - only mark as skipped when explicitly using Skip button
    // Visiting a question doesn't change its status
  };

  const selectOption = (idx, opt) => {
    setAnswers(prev => {
      const copy = [...prev];
      copy[idx] = opt;
      return copy;
    });
    setStatuses(prev => {
      const copy = [...prev];
      copy[idx] = 'answered';
      return copy;
    });
  };

  const setCodeAnswer = (idx, code) => {
    setAnswers(prev => {
      const copy = [...prev];
      copy[idx] = code;
      return copy;
    });
    setStatuses(prev => {
      const copy = [...prev];
      // Only mark as answered if there's sufficient code
      // Otherwise keep current status (don't auto-mark as skipped)
      if (code && code.trim().length > 10) {
        copy[idx] = 'answered';
      }
      // If code is too short, don't change status - let user explicitly skip if needed
      return copy;
    });
  };

  const skipQuestion = (idx) => {
    // mark visited and skipped then jump next
    setVisited(prev => { const c=[...prev]; c[idx]=true; return c; });
    setStatuses(prev => { const c=[...prev]; c[idx]='skipped'; return c; });
    goNext(idx);
  };

  const goNext = (fromIdx = currentIdx) => {
    // Don't change status - only mark as visited
    const next = Math.min(questions.length - 1, fromIdx + 1);
    setCurrentIdx(next);
    markVisited(next);
  };
  const goPrev = () => {
    // Don't change status - only mark as visited
    const prev = Math.max(0, currentIdx - 1);
    setCurrentIdx(prev);
    markVisited(prev);
  };

  const jumpTo = (i) => {
    // Don't change status - only mark as visited
    setCurrentIdx(i);
    markVisited(i);
  };

  // NEW: toggle mark (purple). If already marked -> unmark (restore skipped/visited)
  const toggleMark = (idx) => {
    setStatuses(prev => {
      const copy = [...prev];
      const current = copy[idx];
      if (current === 'marked') {
        // unmark: if visited and no answer -> skipped, if answered -> answered, else not-visited
        if (visited[idx]) {
          copy[idx] = answers[idx] ? 'answered' : 'skipped';
        } else {
          copy[idx] = answers[idx] ? 'answered' : 'not-visited';
        }
      } else {
        copy[idx] = 'marked';
      }
      return copy;
    });
    markVisited(idx);
  };

  const handleSubmit = async (auto = false) => {
    // Prevent double submission
    if (submitted) return;
    
    try {
      // Stop the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Stop desktop proctoring and submit data
      if (isDesktopApp()) {
        console.log("[Desktop] Stopping proctoring and submitting data...");
        try {
          const proctoringResult = await stopDesktopProctoring();
          console.log("[Desktop] Proctoring stopped:", proctoringResult);
          await submitProctoringData(test_id, 'qc');
          console.log("[Desktop] Proctoring data submitted");
        } catch (error) {
          console.error("[Desktop] Error stopping proctoring:", error);
        }
      }

      const payload = {
        test_id,
        language,
        answers,
        meta: { time_left: timeLeft, auto_submitted: auto }
      };
      const res = await axios.post('/api/submit_qc_answers', payload);
      if (res.status === 200) {
        setSubmitted(true);
        localStorage.removeItem(autoSaveKey);
        // Success dialog will be shown (user clicks button to navigate)
      } else {
        setError('Submission failed');
      }
    } catch (err) {
      console.error(err);
      setError('Submission failed.');
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const getStatusIcon = (status, isActive = false, isCurrentQuestion = false) => {
    const baseClasses = "w-4 h-4 shrink-0";
    if (isActive) {
      // When active, use primary-foreground for visibility
      switch (status) {
        case 'answered':
          return <CheckCircle className={cn(baseClasses, "text-primary-foreground")} />;
        case 'skipped':
          return <Circle className={cn(baseClasses, "text-primary-foreground")} />;
        case 'marked':
          return <BookmarkCheck className={cn(baseClasses, "text-primary-foreground")} />;
        default:
          return <Circle className={cn(baseClasses, "text-primary-foreground/60")} />;
      }
    } else {
      // When not active, use theme colors
      switch (status) {
        case 'answered':
          return <CheckCircle className={cn(baseClasses, "text-success")} />;
        case 'skipped':
          return <Circle className={cn(baseClasses, "text-destructive")} />;
        case 'marked':
          return <BookmarkCheck className={cn(baseClasses, "text-primary")} />;
        default:
          // If it's the current question and not answered, show as in progress (yellow)
          if (isCurrentQuestion && status === 'not-visited') {
            return <Circle className={cn(baseClasses, "text-warning")} />;
          }
          return <Circle className={cn(baseClasses, "text-muted-foreground/40")} />;
      }
    }
  };

  const getStatusBadgeVariant = (status, isCurrentQuestion = false) => {
    switch (status) {
      case 'answered':
        return 'success';
      case 'skipped':
        return 'error';
      case 'marked':
        return 'info';
      default:
        // If it's the current question and not answered, show as in progress (warning/yellow)
        if (isCurrentQuestion && status === 'not-visited') {
          return 'warning';
        }
        return 'muted';
    }
  };

  if (loading) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center")}>
        <Text>Loading questions...</Text>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center")}>
        <Text className={cn("text-destructive")}>{error}</Text>
      </div>
    );
  }

  const q = questions[currentIdx] || {};

  return (
    <div className={cn("min-h-screen flex flex-col bg-background")}>
      {/* ===== TOP HEADER BAR - Matching other tests ===== */}
      <header className={cn("flex items-center justify-between px-6 py-4 backdrop-blur-sm border-b border-border min-h-[64px] flex-shrink-0 bg-card/50")}>
        {/* Left: Title + Question Counter */}
        <div className={cn("flex items-center gap-4")}>
          <div className={cn("text-lg font-bold text-primary uppercase tracking-wide")}>AI Evaluator</div>
          <div className={cn("h-6 w-px bg-border")} />
          <Badge variant="secondary" className={cn("text-xs")}>
            Question {currentIdx + 1} of {questions.length}
          </Badge>
        </div>

        {/* Center: Timer */}
        <div className={cn("flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-md border border-border")}>
          <Clock className={cn(
            "w-4 h-4",
            timeLeft <= 60 ? "text-destructive" : timeLeft <= 300 ? "text-warning" : "text-foreground"
          )} />
          <span
            className={cn(
              "font-mono font-semibold text-sm tabular-nums",
              timeLeft <= 60 ? "text-destructive" : timeLeft <= 300 ? "text-warning" : "text-foreground"
            )}
          >
            {formatTime(timeLeft)}
          </span>
        </div>

        {/* Right: Network Status + Actions */}
        <div className={cn("flex items-center gap-3")}>
          <div className={cn("flex items-center gap-2")}>
            <NetworkIcon
              className={cn(
                "w-4 h-4",
                networkStatus === "good"
                  ? "text-success"
                  : networkStatus === "fair"
                  ? "text-warning"
                  : "text-destructive"
              )}
            />
            <span className={cn("text-muted-foreground text-xs")}>
              {networkSpeed ? `${parseFloat(networkSpeed).toFixed(1)} Mbps` : "—"}
            </span>
          </div>
          
          <div className={cn("h-6 w-px bg-border")} />
          
          <div className={cn("flex items-center gap-2")}>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className={cn("h-10 border-border")}
            >
              <X className={cn("w-4 h-4 mr-1.5")} />
              Cancel
            </Button>
            <Button
              onClick={() => handleSubmit(false)}
              disabled={submitted}
              className={cn("h-10 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold px-4")}
            >
              <CheckCircle2 className={cn("w-4 h-4 mr-1.5")} />
              Submit Test
            </Button>
          </div>
        </div>
      </header>

      {/* ===== MAIN CONTENT AREA ===== */}
      <div className={cn("flex-1 flex overflow-hidden")}>
        <aside className={cn("w-72 border-r border-border bg-card p-6 flex flex-col gap-6 overflow-y-auto")}>
          <div>
            <Heading level="h2" className={cn("text-lg font-semibold mb-2 text-foreground")}>
              Quick Coding Test
            </Heading>
            <Text className={cn("text-xs text-muted-foreground")}>
              Multiple choice and coding questions. Navigate between questions using the list below.
            </Text>
          </div>

        <div className={cn("flex-1 overflow-y-auto")}>
          <Text className={cn("text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3")}>
            Questions ({questions.length})
          </Text>
          <div className={cn("space-y-1.5")}>
            {questions.map((qq, i) => {
              const isActive = i === currentIdx;
              const status = statuses[i];
              const isMarked = status === 'marked';
              
              return (
                <Button
                  key={qq.id || i}
                  variant={isActive ? "default" : "ghost"}
                  className={cn(
                    "w-full justify-start h-auto py-2.5 px-3",
                    isActive && "bg-primary text-primary-foreground",
                    isMarked && !isActive && "border-l-4 border-primary bg-primary/5",
                    !isActive && "hover:bg-accent"
                  )}
                  onClick={() => jumpTo(i)}
                >
                  <div className={cn("flex items-center gap-2.5 w-full")}>
                    {getStatusIcon(status, isActive, isActive)}
                    <div className={cn("flex items-center gap-2 flex-1 min-w-0")}>
                      {qq.type === 'mcq' ? (
                        <List className={cn("w-3.5 h-3.5 shrink-0", isActive ? "text-primary-foreground" : "text-muted-foreground")} />
                      ) : (
                        <Code2 className={cn("w-3.5 h-3.5 shrink-0", isActive ? "text-primary-foreground" : "text-muted-foreground")} />
                      )}
                      <span className={cn("text-sm font-medium truncate", isActive ? "text-primary-foreground" : "text-foreground")}>
                        Question {i + 1}
                      </span>
                    </div>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      </aside>

        <main className={cn("flex-1 p-8 overflow-y-auto")}>
          <div className={cn("mb-6")}>
            <div className={cn("flex items-center gap-3 mb-4")}>
              <Badge variant={getStatusBadgeVariant(statuses[currentIdx], true)} className={cn("text-xs")}>
                {statuses[currentIdx] === 'answered' && 'Answered'}
                {statuses[currentIdx] === 'marked' && 'Marked for Review'}
                {statuses[currentIdx] === 'skipped' && 'Skipped'}
                {statuses[currentIdx] === 'not-visited' && 'In Progress'}
              </Badge>
              <Badge variant="outline" className={cn("text-xs")}>
                {q.type === 'mcq' ? (
                  <>
                    <List className={cn("w-3 h-3 mr-1.5")} />
                    Multiple Choice
                  </>
                ) : (
                  <>
                    <Code2 className={cn("w-3 h-3 mr-1.5")} />
                    Coding Task
                  </>
                )}
              </Badge>
            </div>
            <Text className={cn("text-sm text-muted-foreground")}>
              {q.type === 'mcq' ? 'Select the best answer from the options below' : 'Write your solution in the code editor'}
            </Text>
          </div>

        <Card className={cn("shadow-sm")}>
          <CardContent className={cn("p-8")}>
            <Heading level="h2" className={cn("text-xl font-semibold mb-8 text-foreground leading-relaxed")}>
              {q.question || q.title}
            </Heading>

            {q.type === 'mcq' && (
              <RadioGroup
                value={answers[currentIdx] || ''}
                onValueChange={(value) => { selectOption(currentIdx, value); markVisited(currentIdx); }}
                className={cn("space-y-3")}
              >
                {(q.options || []).map((opt, idx) => {
                  const isSelected = answers[currentIdx] === opt;
                  return (
                    <div 
                      key={idx} 
                      className={cn(
                        "flex items-start gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer",
                        isSelected 
                          ? "border-primary bg-primary/5 shadow-sm" 
                          : "border-border bg-card hover:border-primary/50 hover:bg-accent/30"
                      )}
                      onClick={() => selectOption(currentIdx, opt)}
                    >
                      <RadioGroupItem 
                        value={opt} 
                        id={`opt-${currentIdx}-${idx}`}
                        className={cn("mt-0.5")}
                      />
                      <Label 
                        htmlFor={`opt-${currentIdx}-${idx}`} 
                        className={cn("flex-1 cursor-pointer text-foreground leading-relaxed")}
                      >
                        {opt}
                      </Label>
                      {isSelected && (
                        <CheckCircle className={cn("w-5 h-5 text-primary shrink-0")} />
                      )}
                    </div>
                  );
                })}
              </RadioGroup>
            )}

            {q.type === 'code' && (
              <div className={cn("space-y-4")}>
                <div className={cn("space-y-2")}>
                  <Label className={cn("text-sm font-medium")}>Programming Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className={cn("w-[160px] h-9 bg-background border-border text-foreground hover:bg-accent")}>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent className={cn("dark bg-popover border-border text-popover-foreground")}>
                      <SelectItem value="python" className={cn("cursor-pointer focus:bg-accent focus:text-accent-foreground")}>Python</SelectItem>
                      <SelectItem value="java" className={cn("cursor-pointer focus:bg-accent focus:text-accent-foreground")}>Java</SelectItem>
                      <SelectItem value="javascript" className={cn("cursor-pointer focus:bg-accent focus:text-accent-foreground")}>JavaScript</SelectItem>
                      <SelectItem value="c" className={cn("cursor-pointer focus:bg-accent focus:text-accent-foreground")}>C</SelectItem>
                      <SelectItem value="cpp" className={cn("cursor-pointer focus:bg-accent focus:text-accent-foreground")}>C++</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={cn("space-y-2")}>
                  <Label className={cn("text-sm font-medium")}>Your Solution</Label>
                  <Textarea
                    value={answers[currentIdx] || ''}
                    onChange={(e) => { setCodeAnswer(currentIdx, e.target.value); markVisited(currentIdx); }}
                    placeholder={`Write your ${language} solution here...\n\n// Include function signature\n// Add comments for clarity`}
                    className={cn("min-h-[350px] font-mono text-sm")}
                  />
                  <Text className={cn("text-xs text-muted-foreground flex items-center gap-1.5")}>
                    <CheckCircle2 className={cn("w-3.5 h-3.5")} />
                    Auto-saved every 4 seconds
                  </Text>
                </div>
              </div>
            )}

            <Separator className={cn("my-6")} />

            <div className={cn("flex justify-between items-center")}>
              <div className={cn("flex gap-2")}>
                <Button 
                  variant="outline" 
                  onClick={() => skipQuestion(currentIdx)}
                  className={cn("gap-2")}
                >
                  <SkipForward className={cn("w-4 h-4")} />
                  Skip
                </Button>
                <Button 
                  variant="outline" 
                  onClick={goPrev} 
                  disabled={currentIdx === 0}
                  className={cn("gap-2")}
                >
                  <ChevronLeft className={cn("w-4 h-4")} />
                  Previous
                </Button>
              </div>

              <div className={cn("flex gap-2")}>
                <Button
                  variant={statuses[currentIdx] === 'marked' ? "default" : "outline"}
                  onClick={() => toggleMark(currentIdx)}
                  className={cn("gap-2")}
                >
                  {statuses[currentIdx] === 'marked' ? (
                    <>
                      <BookmarkCheck className={cn("w-4 h-4")} />
                      Unmark
                    </>
                  ) : (
                    <>
                      <Bookmark className={cn("w-4 h-4")} />
                      Mark for Review
                    </>
                  )}
                </Button>
                <Button 
                  onClick={() => goNext(currentIdx)} 
                  disabled={currentIdx === questions.length - 1}
                  className={cn("gap-2")}
                >
                  Next
                  <ChevronRight className={cn("w-4 h-4")} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </main>
      </div>

      {/* ===== DEBUG UI PANEL ===== (Dev Only) */}
      {process.env.NODE_ENV === 'development' && (
        <>
          {/* Floating toggle button */}
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className={cn("fixed bottom-4 right-4 z-[99] w-12 h-12 rounded-full bg-warning hover:bg-warning/90 text-warning-foreground shadow-lg flex items-center justify-center transition-all duration-200")}
            title="Toggle Debug Panel (Ctrl+D / Cmd+D)"
          >
            <Bug className={cn("w-5 h-5")} />
          </button>

          {showDebugPanel && (
            <div className={cn("fixed bottom-4 right-4 z-[100] w-96 max-h-[80vh] overflow-y-auto bg-card border-2 border-warning rounded-lg shadow-2xl p-4")}>
          <div className={cn("flex items-center justify-between mb-4 pb-3 border-b border-border")}>
            <div className={cn("flex items-center gap-2")}>
              <Bug className={cn("w-5 h-5 text-warning")} />
              <h3 className={cn("text-foreground font-semibold text-lg")}>Debug UI Panel</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowDebugPanel(false)}
              className={cn("h-8 w-8")}
            >
              <X className={cn("w-4 h-4 text-muted-foreground")} />
            </Button>
          </div>

          <div className={cn("space-y-3")}>
            {/* Question Status Testing Section */}
            <div className={cn("space-y-2")}>
              <h4 className={cn("text-foreground font-medium text-sm mb-2")}>Question Status States</h4>
              
              <Button
                variant="outline"
                size="sm"
                className={cn("w-full justify-start")}
                onClick={() => {
                  selectOption(currentIdx, q.options?.[0] || '');
                }}
                disabled={q.type !== 'mcq' || !q.options?.length}
              >
                <CheckCircle className={cn("w-4 h-4 mr-2")} />
                Set Current to Answered (MCQ)
              </Button>

              <Button
                variant="outline"
                size="sm"
                className={cn("w-full justify-start")}
                onClick={() => {
                  setCodeAnswer(currentIdx, '// Sample answer\nfunction solution() {\n  return true;\n}');
                }}
                disabled={q.type !== 'code'}
              >
                <Code2 className={cn("w-4 h-4 mr-2")} />
                Set Current to Answered (Code)
              </Button>

              <Button
                variant="outline"
                size="sm"
                className={cn("w-full justify-start")}
                onClick={() => {
                  skipQuestion(currentIdx);
                }}
              >
                <SkipForward className={cn("w-4 h-4 mr-2")} />
                Set Current to Skipped
              </Button>

              <Button
                variant="outline"
                size="sm"
                className={cn("w-full justify-start")}
                onClick={() => {
                  toggleMark(currentIdx);
                }}
              >
                {statuses[currentIdx] === 'marked' ? (
                  <>
                    <BookmarkCheck className={cn("w-4 h-4 mr-2")} />
                    Unmark Current Question
                  </>
                ) : (
                  <>
                    <Bookmark className={cn("w-4 h-4 mr-2")} />
                    Mark Current Question
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                className={cn("w-full justify-start")}
                onClick={() => {
                  setAnswers(prev => {
                    const copy = [...prev];
                    copy[currentIdx] = null;
                    return copy;
                  });
                  setStatuses(prev => {
                    const copy = [...prev];
                    copy[currentIdx] = 'not-visited';
                    return copy;
                  });
                }}
              >
                <Circle className={cn("w-4 h-4 mr-2")} />
                Reset Current to Not Visited
              </Button>
            </div>

            {/* Navigation Testing Section */}
            <div className={cn("space-y-2 pt-2 border-t border-border")}>
              <h4 className={cn("text-foreground font-medium text-sm mb-2")}>Navigation</h4>
              
              <div className={cn("grid grid-cols-2 gap-2")}>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full")}
                  onClick={() => jumpTo(0)}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full")}
                  onClick={() => jumpTo(questions.length - 1)}
                >
                  Last
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full")}
                  onClick={() => {
                    const nextIdx = (currentIdx + 1) % questions.length;
                    jumpTo(nextIdx);
                  }}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full")}
                  onClick={() => {
                    const prevIdx = (currentIdx - 1 + questions.length) % questions.length;
                    jumpTo(prevIdx);
                  }}
                >
                  Previous
                </Button>
              </div>

              <Button
                variant="outline"
                size="sm"
                className={cn("w-full justify-start")}
                onClick={() => {
                  const randomIdx = Math.floor(Math.random() * questions.length);
                  jumpTo(randomIdx);
                }}
              >
                <List className={cn("w-4 h-4 mr-2")} />
                Jump to Random Question
              </Button>
            </div>

            {/* Timer Testing Section */}
            <div className={cn("space-y-2 pt-2 border-t border-border")}>
              <h4 className={cn("text-foreground font-medium text-sm mb-2")}>Timer States</h4>
              
              <div className={cn("grid grid-cols-3 gap-2")}>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full text-xs")}
                  onClick={() => setTimeLeft(600)}
                >
                  10:00
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full text-xs")}
                  onClick={() => setTimeLeft(300)}
                >
                  5:00
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full text-xs")}
                  onClick={() => setTimeLeft(60)}
                >
                  1:00
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full text-xs")}
                  onClick={() => setTimeLeft(30)}
                >
                  0:30
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full text-xs")}
                  onClick={() => setTimeLeft(10)}
                >
                  0:10
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("w-full text-xs")}
                  onClick={() => setTimeLeft(5)}
                >
                  0:05
                </Button>
              </div>
            </div>

            {/* Modals & Dialogs Section */}
            <div className={cn("space-y-2 pt-2 border-t border-border")}>
              <h4 className={cn("text-foreground font-medium text-sm mb-2")}>Modals & Dialogs</h4>
              
              <Button
                variant="outline"
                size="sm"
                className={cn("w-full justify-start")}
                onClick={() => setSubmitted(true)}
              >
                <CheckCircle2 className={cn("w-4 h-4 mr-2")} />
                Show Success Dialog
              </Button>

              <Button
                variant="outline"
                size="sm"
                className={cn("w-full justify-start")}
                onClick={() => {
                  setError('Test error message - This is a debug error');
                  setTimeout(() => setError(null), 5000);
                }}
              >
                <AlertTriangle className={cn("w-4 h-4 mr-2")} />
                Show Error Message
              </Button>
            </div>

            {/* Status Indicators Section */}
            <div className={cn("space-y-2 pt-2 border-t border-border")}>
              <h4 className={cn("text-foreground font-medium text-sm mb-2")}>Current State</h4>
              
              <div className={cn("space-y-1.5 text-xs")}>
                <div className={cn("flex justify-between")}>
                  <span className={cn("text-muted-foreground")}>Question:</span>
                  <span className={cn("font-mono")}>{currentIdx + 1} / {questions.length}</span>
                </div>
                <div className={cn("flex justify-between")}>
                  <span className={cn("text-muted-foreground")}>Time Left:</span>
                  <span className={cn("font-mono")}>{formatTime(timeLeft)}</span>
                </div>
                <div className={cn("flex justify-between")}>
                  <span className={cn("text-muted-foreground")}>Status:</span>
                  <Badge variant={getStatusBadgeVariant(statuses[currentIdx], true)} className={cn("text-xs")}>
                    {statuses[currentIdx]}
                  </Badge>
                </div>
                <div className={cn("flex justify-between")}>
                  <span className={cn("text-muted-foreground")}>Type:</span>
                  <span className={cn("font-mono")}>{q.type || 'N/A'}</span>
                </div>
                <div className={cn("flex justify-between")}>
                  <span className={cn("text-muted-foreground")}>Has Answer:</span>
                  <span className={cn("font-mono")}>{answers[currentIdx] ? 'Yes' : 'No'}</span>
                </div>
                <div className={cn("flex justify-between")}>
                  <span className={cn("text-muted-foreground")}>Network:</span>
                  <Badge 
                    variant={networkStatus === 'good' ? 'success' : networkStatus === 'fair' ? 'warning' : 'error'} 
                    className={cn("text-xs")}
                  >
                    {networkStatus}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Question Statuses Overview */}
            <div className={cn("space-y-2 pt-2 border-t border-border")}>
              <h4 className={cn("text-foreground font-medium text-sm mb-2")}>All Questions Status</h4>
              <div className={cn("space-y-1 text-xs max-h-32 overflow-y-auto")}>
                {questions.map((qq, i) => (
                  <div key={i} className={cn("flex items-center justify-between p-1.5 rounded border")}>
                    <span className={cn("font-mono")}>Q{i + 1}</span>
                    <div className={cn("flex items-center gap-2")}>
                      {getStatusIcon(statuses[i], false, i === currentIdx)}
                      <Badge variant={getStatusBadgeVariant(statuses[i], i === currentIdx)} className={cn("text-xs")}>
                        {statuses[i]}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
          )}
        </>
      )}

      {/* Success Dialog */}
      <Dialog open={submitted} onOpenChange={() => {}}>
        <DialogContent className={cn("sm:max-w-[425px] text-center")}>
          <DialogHeader>
            <div className={cn("flex justify-center mb-4")}>
              <div className={cn("w-16 h-16 rounded-full bg-success/10 flex items-center justify-center")}>
                <CheckCircle2 className={cn("w-10 h-10 text-success")} />
              </div>
            </div>
            <DialogTitle className={cn("text-2xl font-bold text-foreground")}>
              Test Submitted Successfully
            </DialogTitle>
            <DialogDescription className={cn("text-muted-foreground mt-2")}>
              Your test has been successfully submitted. Thank you for completing the assessment!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn("flex justify-center mt-6")}>
            <Button onClick={() => navigate('/user-home')} className={cn("gap-2")}>
              <CheckCircle className={cn("w-4 h-4")} />
              Return to Home
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
